/**
 * app.sign365.io - portail client.
 *
 * Navigation par etat plutot que par routeur : le portail compte six
 * ecrans et aucune URL profonde n'a besoin d'etre partageable (le
 * document lui-meme se partage via process.sign365.io, pas ici).
 * Ajouter une dependance de routage pour cela serait payer un cout sans
 * contrepartie.
 */
import { useCallback, useEffect, useState } from "react";

import { api, SESSION_EXPIREE, type Moi } from "./api";
import { session } from "./session";
import { Chargement } from "./components/ui";
import { Login } from "./screens/Login";
import { Inscription } from "./screens/Inscription";
import { InvitationAccept } from "./screens/InvitationAccept";
import { PasswordReset } from "./screens/PasswordReset";
import { Dashboard } from "./screens/Dashboard";
import { Envelopes } from "./screens/Envelopes";
import { EnvelopeDetail } from "./screens/EnvelopeDetail";
import { NewEnvelope } from "./screens/NewEnvelope";
import { Prouver } from "./screens/Prouver";
import { Securiser } from "./screens/Securiser";
import { Billing } from "./screens/Billing";
import { Contacts } from "./screens/Contacts";
import { Settings } from "./screens/Settings";
import { ATraiter } from "./screens/ATraiter";
import type { Duplication } from "./api";

type Vue =
  | { nom: "dashboard" }
  | { nom: "enveloppes" }
  | { nom: "enveloppe"; id: string }
  | { nom: "nouvelle"; produit?: string; depuis?: Duplication }
  | { nom: "a_traiter" }
  | { nom: "securiser" }
  | { nom: "prouver" }
  | { nom: "contacts" }
  | { nom: "facturation" }
  | { nom: "parametres" };

const MENU: { cle: Vue["nom"]; libelle: string }[] = [
  { cle: "dashboard", libelle: "Tableau de bord" },
  // Menu refait par le porteur le 06/10/2026 : les trois fonctions du
  // produit, puis la gestion.
  { cle: "enveloppes", libelle: "Signer" },
  { cle: "securiser", libelle: "Sécuriser" },
  { cle: "prouver", libelle: "Prouver" },
  // Traitement avant signature (12/10/2026) : ce qui attend l'utilisateur.
  { cle: "a_traiter", libelle: "À traiter" },
  // Carnet d'adresses (porteur, 13/10/2026).
  { cle: "contacts", libelle: "Contacts" },
  { cle: "facturation", libelle: "Facturation" },
  { cle: "parametres", libelle: "Paramètres" },
];

export default function App() {
  // Les liens recus par e-mail pointent vers /password-reset?token=… et
  // /invitations/accept?token=… : ce sont les DEUX seules adresses
  // profondes de l'application, d'ou ces tests plutot qu'un routeur
  // complet. La seconde manquait jusqu'au 15/09/2026 - les invitations
  // menaient a un lien mort.
  const [reinitialisation, setReinitialisation] = useState(
    window.location.pathname.startsWith("/password-reset"),
  );
  const [invitation, setInvitation] = useState(
    window.location.pathname.startsWith("/invitations/accept"),
  );
  // Creation de compte par le client (07/10/2026) : /inscription et
  // /inscription/confirmer?token=… (lien recu par e-mail).
  const [inscription, setInscription] = useState(window.location.pathname.startsWith("/inscription"));
  const [connecte, setConnecte] = useState(session.isAuthenticated());
  const [moi, setMoi] = useState<Moi | null>(null);
  const [chargement, setChargement] = useState(connecte);
  // Lien de l'e-mail d'un remplisseur ou d'un valideur : /?a_traiter=1.
  const [vue, setVue] = useState<Vue>(
    new URLSearchParams(window.location.search).has("a_traiter") ? { nom: "a_traiter" } : { nom: "dashboard" });
  // Change a chaque clic de menu : l'ecran est remonte, donc ses donnees
  // (dont le solde) sont relues, meme si c'etait deja l'ecran affiche.
  const [affichage, setAffichage] = useState(0);

  const chargerMoi = useCallback(async () => {
    try {
      setMoi(await api.moi());
    } catch {
      // Jeton invalide ou expire : le client d'API a deja ferme la
      // session, il ne reste qu'a repasser par la connexion.
      session.fermer();
      setConnecte(false);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    if (connecte) void chargerMoi();
  }, [connecte, chargerMoi]);

  useEffect(() => {
    const expiree = () => {
      setConnecte(false);
      setMoi(null);
      setVue({ nom: "dashboard" });
    };
    window.addEventListener(SESSION_EXPIREE, expiree);
    return () => window.removeEventListener(SESSION_EXPIREE, expiree);
  }, []);

  if (invitation) {
    return (
      <InvitationAccept
        onTermine={() => {
          // Le jeton n'a plus cours : inutile de le laisser dans
          // l'historique du navigateur.
          window.history.replaceState({}, "", "/");
          setInvitation(false);
        }}
      />
    );
  }

  if (inscription) {
    return (
      <Inscription
        onTermine={() => {
          window.history.replaceState({}, "", "/");
          setInscription(false);
        }}
      />
    );
  }

  if (reinitialisation) {
    return (
      <PasswordReset
        onTermine={() => {
          // On efface le jeton de l'URL : il n'a plus cours, et le
          // laisser trainer dans l'historique du navigateur n'apporte
          // rien de bon.
          window.history.replaceState({}, "", "/");
          setReinitialisation(false);
        }}
      />
    );
  }

  if (!connecte) {
    return (
      <Login
        onConnecte={() => {
          setConnecte(true);
          setChargement(true);
          setVue(new URLSearchParams(window.location.search).has("a_traiter") ? { nom: "a_traiter" } : { nom: "dashboard" });
        }}
        onCreerCompte={() => {
          window.history.pushState({}, "", "/inscription");
          setInscription(true);
        }}
      />
    );
  }

  if (chargement) return <Chargement libelle="Chargement de votre espace…" />;

  return (
    // L'ecran d'envoi prend toute la largeur : le menu de gauche n'y sert a
    // rien (porteur, 06/10/2026) - on en sort par « Annuler ».
    <div className={vue.nom === "nouvelle" ? "app app--plein" : "app"}>
      <aside className="sidebar" hidden={vue.nom === "nouvelle"}>
        <div className="sidebar__brand">Sign365.io</div>
        {/* Tout le compte au meme endroit, en haut (Franck, 07/10/2026 : « les reunir »). */}
        <div className="sidebar__tenant">
          {moi?.tenant?.name ?? moi?.email}
          {(moi?.tenant?.numero ?? moi?.tenant?.slug) && <div>Compte n° {moi?.tenant?.numero ?? moi?.tenant?.slug}</div>}
          {moi?.tenant?.name && moi?.email && <div>{moi.email}</div>}
          {/* 08/10/2026 : changer de compte sans se reconnecter ; creer le sien
              quand on n'est que membre des comptes d'autrui. */}
          {(moi?.comptes?.length ?? 0) > 1 && (
            <select className="select" aria-label="Changer de compte" style={{ margin: "0.4rem 0", width: "100%" }}
                    value={moi?.tenant?.numero ?? ""}
                    onChange={(e) => {
                      const numero = Number(e.target.value);
                      void api.changerDeCompte(numero).then((r) => {
                        session.ouvrir(r.access_token, String(numero));
                        window.location.reload();
                      });
                    }}>
              {moi?.comptes?.map((c) => <option key={c.numero} value={c.numero}>{c.nom} — n° {c.numero}</option>)}
            </select>
          )}
          {moi && moi.proprietaire === false && (
            <button type="button" className="navlink" style={{ padding: "0.4rem 0" }}
                    onClick={() => void api.creerMonComptePersonnel().then((r) => {
                      session.ouvrir(r.access_token, String(r.numero));
                      window.location.reload();
                    })}>
              Créer mon compte
            </button>
          )}
          <button
            type="button"
            className="navlink"
            style={{ padding: "0.4rem 0" }}
            onClick={() => {
              session.fermer();
              setConnecte(false);
              setMoi(null);
            }}
          >
            Se déconnecter
          </button>
        </div>
        <nav>
          {MENU.map((m) => (
            <button
              key={m.cle}
              type="button"
              className="navlink"
              aria-current={vue.nom === m.cle ? "page" : undefined}
              onClick={() => {
                setVue({ nom: m.cle } as Vue);
                // Recliquer sur l'ecran deja ouvert le relit aussi (solde).
                setAffichage((n) => n + 1);
              }}
            >
              {m.libelle}
            </button>
          ))}
        </nav>
      </aside>

      <main className="main" key={affichage}>
        {vue.nom === "dashboard" && (
          <Dashboard
            onOuvrirEnveloppe={(id) => setVue({ nom: "enveloppe", id })}
            onNouvelleEnveloppe={() => setVue({ nom: "nouvelle" })}
            onOuvrirFacturation={() => setVue({ nom: "facturation" })}
            onOuvrirATraiter={() => setVue({ nom: "a_traiter" })}
          />
        )}
        {vue.nom === "enveloppes" && (
          <Envelopes
            onOuvrir={(id) => setVue({ nom: "enveloppe", id })}
            onNouvelle={(produit) => setVue({ nom: "nouvelle", produit })}
          />
        )}
        {vue.nom === "enveloppe" && (
          <EnvelopeDetail envelopeId={vue.id} onRetour={() => setVue({ nom: "enveloppes" })}
                          onDupliquer={(depuis) => setVue({ nom: "nouvelle", depuis })} />
        )}
        {vue.nom === "nouvelle" && (
          <NewEnvelope
            produitInitial={vue.produit}
            depuis={vue.depuis}
            // Porteur, 12/10/2026 : apres l'envoi, retour a la liste des enveloppes.
            onCree={() => setVue({ nom: "enveloppes" })}
            onAnnuler={() => setVue({ nom: "enveloppes" })}
          />
        )}
        {vue.nom === "securiser" && <Securiser />}
        {vue.nom === "prouver" && <Prouver />}
        {vue.nom === "a_traiter" && <ATraiter />}
        {vue.nom === "contacts" && <Contacts />}
        {vue.nom === "facturation" && <Billing role={moi?.role} />}
        {vue.nom === "parametres" && <Settings />}
      </main>
    </div>
  );
}
